import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  GridLegacy as Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useModulesConfig } from '~/data/company';
import {
  AdminWorkspaceRoles,
  Roles,
  getAdminWorkspaceRolesFromRoles,
} from '~/data/user/role';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  getAvailableWidgets,
  getWorkspaceRoleTranslationKey,
  getWorkspaceRolesbyUserRoles,
} from '~/scenes/MyWorkspace/components/Widgets/data';
import {
  WorkspaceRestrictionInfo,
  WorkspaceRestrictionState,
} from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceRestrictionInfo';
import {
  WorkspaceSetupFormSettings,
  WorkspaceSetupMode,
  WorkspaceTemplate_Empty,
  initializeNewWorkspace,
  useWorkspaceSetupForm,
  useWorkspaceTemplates,
  yupValidateWorkspaceName,
} from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceToolbarReadMode/components/WorkspaceSetupDialog';
import { WorkspaceTypeIcon } from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceTypeIcon';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import { WorkspaceSettings } from '~/scenes/MyWorkspace/userSettings';
import { isRoleHRAdminFull } from '~/services/Permissions';
import { themeColors } from '~/theme';

import { WorkspaceRoleIcon } from '../../../WorkspaceRoleIcon';

export type IFormInput = {
  type: string;
  name: string;
  template: string;
  restrictToRoles: string[];
  role?: AdminWorkspaceRoles;
};

type PropsType = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  workspaces: WorkspaceSettings[];
  formSettings: WorkspaceSetupFormSettings | undefined;
};

type WorkspaceTypeCounter = {
  [p: string]: AdminWorkspaceRoles[];
};

type WorkspaceTypeTotals = {
  [p: string]: {
    [key in AdminWorkspaceRoles]?: number;
  };
};

export const WorkspaceSetupDialog = (props: PropsType) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { open, onClose, formSettings, workspaces } = props;
  const myRoles = useLoggedInUserRoles() as Roles[];
  const { isModuleTabEnabled, isModuleEnabled } = useModulesConfig();
  const { getWorkspaceTemplateByRole } = useWorkspaceTemplates();
  const { applicationConfig } = useApplicationConfig();

  const isHRAdmin = useMemo(() => isRoleHRAdminFull(myRoles), [myRoles]);

  const workspaceTotals = useMemo(() => {
    const getRoleTotals = (roles: AdminWorkspaceRoles[]) => {
      return Object.values(AdminWorkspaceRoles).reduce((totals, role) => {
        return {
          ...totals,
          [role]: roles.filter((r) => r === role).length,
        };
      }, {} as { [key in AdminWorkspaceRoles]?: number });
    };

    return Object.entries(
      workspaces.reduce((acc, workspace) => {
        const { type, restrictToRoles } = workspace;

        if (restrictToRoles.length < 1 || !type) {
          return acc;
        }

        const previousRoles: AdminWorkspaceRoles[] = acc[type] ?? [];

        return {
          ...acc,
          [type]: [...previousRoles, ...restrictToRoles],
        };
      }, {} as WorkspaceTypeCounter),
    ).reduce((totals, [type, roles]) => {
      return {
        ...totals,
        [type]: getRoleTotals(roles),
      };
    }, {} as WorkspaceTypeTotals);
  }, [workspaces]);

  const isRoleOptionDisabled = useCallback(
    (type: string, role: AdminWorkspaceRoles) => {
      const maxCompany =
        applicationConfig.fComConfig.miscellaneousConfig
          .fComCompanyWorkspacesLimit;
      const maxPersonal =
        applicationConfig.fComConfig.miscellaneousConfig
          .fComPersonalWorkspacesLimit;
      const max: number = type === 'company' ? maxCompany : maxPersonal;
      const count: number = workspaceTotals[type]?.[role] ?? 0;

      return count >= max;
    },
    [
      applicationConfig.fComConfig.miscellaneousConfig
        .fComCompanyWorkspacesLimit,
      applicationConfig.fComConfig.miscellaneousConfig
        .fComPersonalWorkspacesLimit,
      workspaceTotals,
    ],
  );

  const { formMode, initialWorkspaceSettings, createWorkspace } =
    useWorkspaceSetupForm({
      formSettings,
    });

  const validationSchema = useMemo(() => {
    return yup.object().shape<IFormInput>({
      type: yup.string(),
      name: yupValidateWorkspaceName(
        workspaces,
        initialWorkspaceSettings.id,
        t,
      ),
      template: yup.string(),
      restrictToRoles: yup.array().of(yup.string().required()).required(),
      role: yup.string(),
    });
  }, [workspaces, initialWorkspaceSettings, t]);
  const resolver = useYupValidationResolver(validationSchema);

  const initialFieldValues: IFormInput = useMemo(() => {
    const myAdminRoles = getAdminWorkspaceRolesFromRoles(myRoles);
    const role = myAdminRoles.length === 1 ? myAdminRoles[0] : undefined;

    return {
      type: WorkspaceType.Personal,
      name: initialWorkspaceSettings.name || '',
      restrictToRoles: [],
      template: WorkspaceTemplate_Empty,
      role,
    };
  }, [initialWorkspaceSettings, myRoles]);

  const dialogTitle =
    formMode === WorkspaceSetupMode.Duplicate
      ? `${t('duplicateWorkspace')}: ${initialWorkspaceSettings.name}`
      : t('createWorkspace');

  const form = useForm<IFormInput>({
    mode: 'all',
    defaultValues: initialFieldValues,
    resolver: resolver as never,
  });

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form;

  const hasErrors = Object.keys(errors).length > 0;

  const [selectedRole, selectedType] = useWatch({
    control,
    name: ['role', 'type'],
  });

  useEffect(() => {
    if (
      !!selectedType &&
      !!selectedRole &&
      isRoleOptionDisabled(selectedType, selectedRole)
    ) {
      setValue('role', undefined);
    }
  }, [isRoleOptionDisabled, selectedType, selectedRole, setValue]);

  const { workspaceRoleOptions } = useMemo(() => {
    const workspaceRoleOptions: AdminWorkspaceRoles[] =
      isHRAdmin && selectedType === WorkspaceType.Company
        ? Object.values(AdminWorkspaceRoles)
        : getWorkspaceRolesbyUserRoles(myRoles);

    return {
      workspaceRoleOptions,
    };
  }, [isHRAdmin, myRoles, selectedType]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const filterIncompatibleWidgets = useCallback(
    (
      workspace: WorkspaceSettings,
      restrictedToRoles: AdminWorkspaceRoles[],
    ): WorkspaceSettings => {
      if (restrictedToRoles.length <= 0) {
        return workspace;
      }

      const availableWidgets = getAvailableWidgets({
        workspaceRoles: restrictedToRoles,
        isModuleEnabled,
        isModuleTabEnabled,
      });

      return {
        ...workspace,
        widgets: workspace.widgets.filter((ws) =>
          availableWidgets.includes(ws.widget),
        ),
      };
    },
    [isModuleEnabled, isModuleTabEnabled],
  );

  const handleFormSubmit = useMemo(() => {
    return handleSubmit(
      (formData) => {
        const restrictedToRoles = !!formData.role ? [formData.role] : [];
        const newWorkspace = filterIncompatibleWidgets(
          formMode === WorkspaceSetupMode.Duplicate
            ? initialWorkspaceSettings
            : getWorkspaceTemplateByRole(
                formData?.role ?? AdminWorkspaceRoles.EMPLOYEE,
              ),
          restrictedToRoles,
        );

        (async () => {
          try {
            createWorkspace({
              ...initializeNewWorkspace(newWorkspace),
              name: formData.name,
              type: formData.type,
              restrictToRoles: restrictedToRoles,
            });
          } catch (error) {
            enqueueSnackbar(t('errorMsg'), {
              variant: 'error',
            });
          }
        })();
      },
      (errors) => {
        // TODO: Create better & generic form validation error alert/dialog/popup thing
        // console.log(errors, 'handleSubmit errors');
        Object.values(errors).forEach((fieldError) => {
          enqueueSnackbar(fieldError.message, {
            variant: 'error',
          });
        });
      },
    );
  }, [
    filterIncompatibleWidgets,
    getWorkspaceTemplateByRole,
    formMode,
    createWorkspace,
    initialWorkspaceSettings,
    enqueueSnackbar,
    handleSubmit,
    t,
  ]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <Box sx={{ overflow: 'auto', minWidth: '420px' }}>
        <Grid
          container
          direction={'column'}
          spacing={2}
          sx={{
            padding: theme.spacing(1, 2),
          }}
        >
          <Grid item>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                return (
                  <TextField
                    fullWidth
                    value={value}
                    label={t('workspaceName')}
                    onChange={onChange}
                    required={true}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid>
          {isHRAdmin ? (
            <Grid item>
              <Box
                sx={{
                  backgroundColor: themeColors.background.grey,
                  padding: '1px',
                  borderRadius: theme.spacing(0.5),
                }}
              >
                <Grid
                  container
                  direction={'column'}
                  spacing={2}
                  sx={{
                    padding: theme.spacing(1, 2),
                  }}
                >
                  <Grid item>
                    <Typography variant={'subtitle1'}>
                      {t('permanentSettings')}
                    </Typography>
                    <Typography
                      fontSize={'0.85rem'}
                      fontWeight={300}
                      variant={'caption'}
                    >
                      {t('permanentSettingsDescription')}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Divider />
                  </Grid>
                  <Grid item>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field: { onChange, value } }) => {
                        const renderIconLabel = (
                          type: WorkspaceType | string,
                          label: string,
                        ) => {
                          return (
                            <>
                              <WorkspaceTypeIcon
                                type={type}
                                sx={{
                                  verticalAlign: 'text-top',
                                  marginRight: theme.spacing(0.5),
                                }}
                                fontSize={'inherit'}
                              />
                              {label}
                            </>
                          );
                        };

                        return (
                          <>
                            <FormControl>
                              <FormLabel id="demo-radio-buttons-group-label">
                                {t('workspaceType')}
                              </FormLabel>
                              <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                onChange={onChange}
                                value={value}
                                row
                              >
                                <FormControlLabel
                                  value={WorkspaceType.Personal}
                                  control={<Radio />}
                                  label={renderIconLabel(
                                    WorkspaceType.Personal,
                                    t('workspaceType_Personal'),
                                  )}
                                />
                                <FormControlLabel
                                  value={WorkspaceType.Company}
                                  control={<Radio />}
                                  label={renderIconLabel(
                                    WorkspaceType.Company,
                                    t('workspaceType_Company'),
                                  )}
                                />
                              </RadioGroup>
                            </FormControl>
                          </>
                        );
                      }}
                    />
                  </Grid>
                  {selectedType === WorkspaceRestrictionState.Personal && (
                    <Grid item>
                      <WorkspaceRestrictionInfo
                        restrictionType={WorkspaceRestrictionState.Personal}
                        sx={{ marginBottom: theme.spacing(1) }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
          ) : (
            <Grid item>
              <WorkspaceRestrictionInfo
                restrictionType={WorkspaceRestrictionState.Personal}
              />
            </Grid>
          )}

          <Box sx={{ padding: theme.spacing(2) }}>
            <Typography>{t('workspaceSelectRoleTitle')}</Typography>
            <Controller
              name="role"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    onChange={onChange}
                    value={value}
                    sx={{ padding: theme.spacing(2) }}
                  >
                    {workspaceRoleOptions.map((role) => {
                      return (
                        <FormControlLabel
                          key={role}
                          value={role}
                          control={<Radio />}
                          disabled={isRoleOptionDisabled(selectedType, role)}
                          label={
                            <Box sx={{ display: 'flex' }}>
                              {t(getWorkspaceRoleTranslationKey(role))}
                              <WorkspaceRoleIcon
                                type={role}
                                sx={{
                                  verticalAlign: 'text-top',
                                  marginLeft: theme.spacing(2),
                                }}
                              />
                            </Box>
                          }
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              )}
            />
            <Typography
              fontSize={'0.85rem'}
              fontWeight={300}
              variant={'caption'}
              component={'p'}
            >
              {t('widgetsSelectedInfo')}
            </Typography>
          </Box>
        </Grid>
      </Box>
      <DialogActions>
        <Button onClick={handleClose}>{t('laCancel')}</Button>
        <Button
          onClick={handleFormSubmit}
          disabled={hasErrors || !selectedRole}
        >
          {t('laContinue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
